import React, { useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import Logo from '@comp/Logo/Logo';
import useWindowSize from '@hooks/useWindowSize';
import { segment } from '@actions/analytics';
import HeaderTop from '@layout/Components/HeaderContainer';
import HeaderContent from '@layout/Components/HeaderParent';
import Dynamic from '@base/Dynamic/Dynamic';
import Divider from '@base/Divider/Divider';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { getIsLoggedIn } from '@api/selectors/auth';
import Button from '@unicons-base/Button/Button';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { UN_ICONS_BUTTON_VARIANT } from 'types/unacademyIcons/button.type';
import IconsHeaderNotification from '@layout/Header/HeaderNotification';
import useTimeout from '@hooks/useTimeout';
import { LESSON_PATHNAME } from '@constants/unacademyIcons/icons.constants';
import { LOGIN_STARTED } from '@cont/Login/constants';
import useMyInfo from '@api/hooks/auth/useMyInfo';

const HeaderUser = Dynamic(
  () => import('@layout/Header/HeaderUser/HeaderUser')
) as any;
const GoalSelector = Dynamic(
  () => import('@layout/Header/GoalSelector/HeaderGoalSelector')
);

interface IconsHeaderProps {
  isCheckoutUrl?: boolean;
}

const HeaderContainer = styled(HeaderTop)<{ $isLessonPlayer?: boolean }>`
  background: var(--color-i-all-black);
  z-index: ${({ $isLessonPlayer }) => ($isLessonPlayer ? '2000' : '3')};
  ${mediaQuery.uptoTablet} {
    height: 72px;
  }
  ${mediaQuery.uptoMobile} {
    display: block;
    width: 100%;
    height: 64px;
  }
`;

const HeaderParent = styled(HeaderContent)`
  ${mediaQuery.uptoTablet} {
    width: 100%;
    padding: 0 16px;
  }
  ${mediaQuery.uptoMobile} {
    display: flex;
    width: 100%;
    padding: 16px;
    justify-content: space-between;
  }
`;

const StyledDivider = styled(Divider)`
  &.MuiDivider-root {
    margin: 0 ${Spacings.SPACING_6B};
    height: 24px;
    width: 2px;
    border-radius: ${Spacings.SPACING_2};
    ${mediaQuery.uptoTablet} {
      margin: 0 ${Spacings.SPACING_6B};
    }
  }
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const RightContentWrapper = styled.div`
  display: flex;
  margin-left: auto;
`;

const HeaderUserContainer = styled.div`
  min-width: ${Spacings.SPACING_10B};
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const StyledLoginAction = styled(Button)`
  height: 40px;
  min-height: 40px;
  color: var(--color-i-icon-light-tertiary);
  border-color: var(--color-i-icon-dark-base);
  font-size: ${Fonts.H6};
  ${mediaQuery.uptoMobile} {
    height: 32px;
    min-height: 32px;
    font-size: ${Fonts.ULTRA_SMALL};
  }
  &:hover {
    color: var(--color-i-icon-light-tertiary);
    background-color: var(--color-i-all-black);
  }
`;

const StyledLogo = styled(Logo)`
  height: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoTablet} {
    height: ${Spacings.SPACING_9B};
  }
`;

const isSubscriptionPricingsUrl = (route) =>
  route === '/goal/[goalSlug]/[goalUID]/subscribe';

const hideLoginButtonUrls = [
  '/course/[slug]/[courseUID]',
  '/lesson/[lessonSlug]/[lessonUID]'
];

const IconsHeader: React.FC<IconsHeaderProps> = ({ isCheckoutUrl }) => {
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const [mounted, setMounted] = useState(false);

  const router = useRouter();
  const { route } = router;
  const { isMobile } = useWindowSize();
  const displayGoalSelector = (isLoggedIn && isMobile) || !isMobile;

  const isLessonPlayer = useMemo(
    () => router.pathname === LESSON_PATHNAME,
    [router]
  );

  useTimeout(() => setMounted(true), 0);

  const displayNotification = isLoggedIn && mounted && !isCheckoutUrl;

  const hideLoginButton = hideLoginButtonUrls.includes(route);

  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const handleLogin = () => {
    segment.track(LOGIN_STARTED);
    openLoginDrawer();
  };

  const renderLoginAction = () => {
    if (hideLoginButton && isMobile) return null;

    return (
      <StyledLoginAction
        variant={UN_ICONS_BUTTON_VARIANT.HOLLOW}
        bgColor="var(--color-i-all-black)"
        onClick={handleLogin}
        width="80px"
      >
        Login
      </StyledLoginAction>
    );
  };

  return (
    <HeaderContainer $isLessonPlayer={isLessonPlayer}>
      <HeaderParent>
        {isCheckoutUrl && !isSubscriptionPricingsUrl(route) ? (
          <StyledLogo disabled isUnIcons />
        ) : (
          <StyledLogo isReload={isMobile} isUnIcons />
        )}
        {displayGoalSelector && (
          <>
            <StyledDivider orientation="vertical" />
            <GoalSelector />
          </>
        )}
        {!isMobile && (
          <RightContentWrapper>
            {displayNotification && <IconsHeaderNotification isUAIconsGoal />}
            {!isCheckoutUrl && (
              <>
                {isLoggedIn ? (
                  <HeaderUserContainer>
                    <HeaderUser
                      isEducatorUrl={false}
                      isHeaderTransparent={false}
                      isUAIconsGoal
                    />
                  </HeaderUserContainer>
                ) : (
                  renderLoginAction()
                )}
              </>
            )}
          </RightContentWrapper>
        )}
        {isMobile && !isLoggedIn && !isCheckoutUrl && renderLoginAction()}
      </HeaderParent>
    </HeaderContainer>
  );
};

IconsHeader.displayName = 'IconsHeader';

export default IconsHeader;
